// ABOUTME: redux slice for managing super admin user impersonation state
// ABOUTME: tracks real user, impersonated user, and impersonation status

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RootState } from "../store";
import { UserRole } from "./authSlice";

interface User {
  _id?: string;
  roles: UserRole[];
  role?: string; // keep for backward compatibility
  email?: string;
  firstName?: string;
  lastName?: string;
  tenantId?: string;
  tenantSlug?: string;
}

interface ImpersonationResponse {
  success: boolean;
  token: string;
  impersonatedUser: ApiUser;
  tenantSlug?: string;
  message: string;
}

interface StopImpersonationResponse {
  success: boolean;
  token: string;
  user: User;
  message: string;
}

interface ApiUser {
  _id: string;
  roles: UserRole[];
  role?: string; // keep for backward compatibility
  email: string;
  tenantSlug?: string;
  firstName?: string;
  lastName?: string;
  tenantId?: string;
}

interface UsersListResponse {
  success: boolean;
  users: ApiUser[];
  groupedByTenant: Record<string, ApiUser[]>;
}

interface ImpersonationState {
  isImpersonating: boolean;
  realUser: User | null;
  impersonatedUser: User | null;
  originalToken: string | null;
}

// check localStorage for persisted impersonation state
const storedImpersonation = localStorage.getItem("impersonation");
const storedToken = localStorage.getItem("token");

let parsedImpersonation = null;
if (storedImpersonation && storedToken) {
  try {
    parsedImpersonation = JSON.parse(storedImpersonation);
    // validate that the current token matches the impersonation token
    // if not, the user logged out and back in, so clear impersonation
    if (parsedImpersonation?.isImpersonating) {
      // decode the current token to check if it's an impersonation token
      const tokenPayload = JSON.parse(atob(storedToken.split(".")[1]));
      if (!tokenPayload.isImpersonating) {
        // current token is not an impersonation token, clear state
        parsedImpersonation = null;
        localStorage.removeItem("impersonation");
      }
    }
  } catch {
    // if parsing fails, clear the stored state
    parsedImpersonation = null;
    localStorage.removeItem("impersonation");
  }
}

const initialState: ImpersonationState = {
  isImpersonating: parsedImpersonation?.isImpersonating || false,
  realUser: parsedImpersonation?.realUser || null,
  impersonatedUser: parsedImpersonation?.impersonatedUser || null,
  originalToken: parsedImpersonation?.originalToken || null,
};

const impersonationSlice = createSlice({
  name: "impersonation",
  initialState,
  reducers: {
    startImpersonation(
      state,
      action: PayloadAction<{
        realUser: User;
        impersonatedUser: User;
        originalToken: string;
      }>,
    ) {
      state.isImpersonating = true;
      state.realUser = action.payload.realUser;
      state.impersonatedUser = action.payload.impersonatedUser;
      state.originalToken = action.payload.originalToken;

      // persist to localStorage
      localStorage.setItem(
        "impersonation",
        JSON.stringify({
          isImpersonating: true,
          realUser: action.payload.realUser,
          impersonatedUser: action.payload.impersonatedUser,
          originalToken: action.payload.originalToken,
        }),
      );
    },
    stopImpersonation(state) {
      state.isImpersonating = false;
      state.realUser = null;
      state.impersonatedUser = null;
      state.originalToken = null;

      // clear from localStorage
      localStorage.removeItem("impersonation");
    },
    validateImpersonationState(state) {
      // validate that the current token is still an impersonation token
      const storedToken = localStorage.getItem("token");
      if (state.isImpersonating && storedToken) {
        try {
          const tokenPayload = JSON.parse(atob(storedToken.split(".")[1]));
          if (!tokenPayload.isImpersonating) {
            // current token is not an impersonation token, clear state
            state.isImpersonating = false;
            state.realUser = null;
            state.impersonatedUser = null;
            state.originalToken = null;
            localStorage.removeItem("impersonation");
          }
        } catch {
          // if token parsing fails, clear impersonation state
          state.isImpersonating = false;
          state.realUser = null;
          state.impersonatedUser = null;
          state.originalToken = null;
          localStorage.removeItem("impersonation");
        }
      }
    },
  },
});

export const {
  startImpersonation,
  stopImpersonation,
  validateImpersonationState,
} = impersonationSlice.actions;

// api endpoints for impersonation
export const impersonationApi = createApi({
  reducerPath: "impersonationApi",
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["ImpersonationUsers"],
  endpoints: (builder) => ({
    getImpersonatableUsers: builder.query<UsersListResponse, void>({
      query: () => "/impersonate/users",
      providesTags: ["ImpersonationUsers"],
    }),
    startImpersonating: builder.mutation<ImpersonationResponse, string>({
      query: (userId) => ({
        url: `/impersonate/start/${userId}`,
        method: "POST",
      }),
    }),
    stopImpersonating: builder.mutation<StopImpersonationResponse, void>({
      query: () => ({
        url: "/impersonate/stop",
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetImpersonatableUsersQuery,
  useStartImpersonatingMutation,
  useStopImpersonatingMutation,
} = impersonationApi;

export default impersonationSlice.reducer;
